/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * The controls a profile field is drawn with, shared by the profile dialog and the feedback dialog
 * so a dealership address looks and behaves the same in both. What a field *is* lives in
 * `@/lib/profileField`; this is only how it is put on screen.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Select } from '@/design-system';
import type { GeoOption } from '@/lib/geoOptions';
import {
  fieldId,
  labelFor,
  type FieldOptions,
  type FieldSource,
  type ResolvedField,
} from '@/lib/profileField';
import { Icon } from '@/components/Icon';

const SOURCE_HINT: Record<FieldSource, string> = {
  account: 'profile.fromAccount',
  gdl: 'profile.fromGdl',
  profile: 'profile.fromProfile',
};

/**
 * A field the user cannot type in. The source is a lock glyph inside the box, captioned on hover,
 * rather than text beside the label: at eight locked fields those captions pushed the labels onto
 * two lines and read like an error. The wording stays in the DOM for screen readers.
 *
 * The label sits in a sibling of the input rather than wrapping it, so the hint never becomes part
 * of the field's accessible name.
 */
export function ReadonlyField({
  label,
  value,
  source,
}: {
  label: string;
  value: string;
  source?: FieldSource | null;
}) {
  const { t } = useTranslation();
  const id = fieldId(label);
  const hint = source ? t(SOURCE_HINT[source]) : '';
  return (
    <div className="pf-field">
      <span className="fl">
        <label htmlFor={id}>{label}</label>
      </span>
      <span className="pf-locked">
        <input
          id={id}
          className="pf-in"
          value={value}
          readOnly
          aria-readonly="true"
          aria-describedby={source ? `${id}-src` : undefined}
        />
        {source && (
          <span className="pf-lock" title={hint}>
            <Icon name="lock" size={12} />
            <span className="pf-sr" id={`${id}-src`}>
              {hint}
            </span>
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * A text box the user owns.
 *
 * Format complaints wait for a blur or a save attempt, so the first keystroke of an e-mail address
 * is never an error. Each field tracks its own blur; `revealErrors` is how a submit shows every
 * complaint at once instead of rejecting the form one field at a time.
 */
export function EditableField({
  label,
  value,
  onChange,
  revealErrors,
  ...opts
}: FieldOptions & {
  label: string;
  value: string;
  onChange: (value: string) => void;
  revealErrors?: boolean;
}) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);
  const id = fieldId(label);
  const empty = !value.trim();
  const missing = Boolean(opts.required) && empty;
  const error = (revealErrors || touched) && opts.invalid ? opts.invalid : null;
  return (
    <div className={`pf-field${missing ? ' pf-miss' : ''}${error ? ' pf-invalid' : ''}`}>
      <span className="fl">
        <label htmlFor={id}>{label}</label>
        {opts.required && empty && <span className="pf-req">{t('profile.required')}</span>}
        {opts.optional && <span className="pf-opt">{t('profile.optional')}</span>}
      </span>
      <input
        id={id}
        className="pf-in"
        value={value}
        placeholder={opts.placeholder}
        inputMode={opts.inputMode}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-err` : undefined}
      />
      {error && (
        <span className="pf-err" id={`${id}-err`}>
          {error}
        </span>
      )}
    </div>
  );
}

/** Read-only when the system of record answered, a plain text box when it did not. */
export function TextField({
  field,
  ...rest
}: FieldOptions & {
  label: string;
  field: ResolvedField;
  onChange: (value: string) => void;
  revealErrors?: boolean;
}) {
  return field.locked ? (
    <ReadonlyField label={rest.label} value={field.value} source={field.source} />
  ) : (
    <EditableField {...rest} value={field.value} />
  );
}

/**
 * A picker instead of a text box, so an address is chosen from ISO rather than typed. Falls back to
 * the read-only display when GDL already answered.
 *
 * The design-system picker rather than a native `<select>`: it matches every other dropdown in the
 * app, and its list is a portalled panel that scrolls and takes type-ahead, which a browser's own
 * control does poorly at 250 countries. It is a button, so the caption is a plain span and the
 * accessible name comes from `ariaLabel`.
 */
export function SelectField({
  label,
  field,
  options,
  onChange,
  placeholder,
  required,
  disabled,
}: {
  label: string;
  field: ResolvedField;
  options: GeoOption[];
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  if (field.locked) {
    return (
      <ReadonlyField label={label} value={labelFor(options, field.value)} source={field.source} />
    );
  }
  const missing = Boolean(required) && !field.value.trim();
  // A value the current list does not offer shows as unset, so the picker asks again instead of
  // displaying a country that the chosen state contradicts.
  const value = options.some((o) => o.code === field.value) ? field.value : '';
  return (
    <div className={`pf-field${missing ? ' pf-miss' : ''}`}>
      <span className="fl">
        <span>{label}</span>
        {missing && <span className="pf-req">{t('profile.required')}</span>}
      </span>
      <Select
        value={value}
        onValueChange={onChange}
        options={options.map((o) => ({ value: o.code, label: o.name }))}
        placeholder={placeholder}
        disabled={disabled}
        ariaLabel={label}
        className="pf-sel"
      />
    </div>
  );
}
